import { Injectable, InjectionToken, inject, signal } from '@angular/core';

/** Qualite de la connexion d'un participant, telle que LiveKit la rapporte. */
export type QualiteConnexion = 'excellent' | 'good' | 'poor' | 'lost' | 'unknown';

export type EtatSalle = 'inactive' | 'connexion' | 'connectee' | 'reconnexion' | 'coupee' | 'erreur';

/** Un participant de la salle, soi-meme compris, avec ce que l'ecran en montre. */
export interface ParticipantSalle {
  identity: string;
  moi: boolean;
  /** Camera (ou partage d'ecran distant) : piste video a afficher, s'il y en a une. */
  videoTrack: MediaStreamTrack | null;
  /** Piste audio DISTANTE a jouer. Jamais celle de soi-meme (larsen). */
  audioTrack: MediaStreamTrack | null;
  micro: boolean;
  camera: boolean;
  parle: boolean;
  qualite: QualiteConnexion;
}

/** Ce que `SalleAppel` utilise du client de salle LiveKit (`LegbaClientRoom`, `@legba-core/realtime/client`). */
export interface SalleClient {
  connect(options: { serverUrl: string; token: string }): Promise<void>;
  disconnect(): Promise<void>;
  publishMicrophone(): Promise<void>;
  unpublishMicrophone(): Promise<void>;
  publishCamera(): Promise<void>;
  unpublishCamera(): Promise<void>;
  startScreenShare(): Promise<void>;
  stopScreenShare(): Promise<void>;
  switchCameraDevice(deviceId: string): Promise<void>;
  getRemoteParticipants(): { identity: string; audioTrack?: MediaStreamTrack; videoTrack?: MediaStreamTrack; screenShareTrack?: MediaStreamTrack }[];
  on(event: 'participantConnected' | 'participantDisconnected' | 'trackSubscribed' | 'trackUnsubscribed' | 'reconnecting' | 'reconnected' | 'disconnected', listener: () => void): void;
  /** Echappatoire typee vers la `Room` de livekit-client, pour ce que l'enveloppe Legba n'expose pas (parole, sourdine, qualite). */
  raw: SalleBrute;
}

interface ParticipantBrut {
  identity: string;
  isMicrophoneEnabled: boolean;
  isCameraEnabled?: boolean;
  isSpeaking: boolean;
  connectionQuality: string;
}

export interface SalleBrute {
  localParticipant: ParticipantBrut & {
    isScreenShareEnabled: boolean;
    getTrackPublication(source: string): { track?: { mediaStreamTrack: MediaStreamTrack } } | undefined;
  };
  remoteParticipants: ReadonlyMap<string, ParticipantBrut>;
  on(event: string, listener: (...args: unknown[]) => void): void;
}

/** Evenements bruts qui changent ce que l'ecran montre sans passer par l'enveloppe Legba. */
const EVENEMENTS_BRUTS = [
  'trackMuted',
  'trackUnmuted',
  'activeSpeakersChanged',
  'connectionQualityChanged',
  'localTrackPublished',
  'localTrackUnpublished',
] as const;

/** Fabrique du client de salle — substituable en test. Charge le SDK a la demande : il est lourd et propre au navigateur. */
export const FABRIQUE_SALLE = new InjectionToken<() => Promise<SalleClient>>('FABRIQUE_SALLE', {
  providedIn: 'root',
  factory: () => async () => {
    const { createLegbaRoom } = await import('@legba-core/realtime/client');
    return createLegbaRoom() as unknown as SalleClient;
  },
});

export type Avertissement = 'micro_refuse' | 'camera_refusee' | null;

export interface OptionsRejoindre {
  roomName: string;
  /** `token-endpoint` deja signe pour CET appel — voir `CallService.mintCallAuthUrl`. */
  authUrl: string;
  /** Allumer la camera a l'arrivee (appel video repondu en entier, ou lance par soi). */
  camera: boolean;
}

/**
 * LA SALLE LIVEKIT DE L'APPEL EN COURS.
 *
 * Remplace `<legba-call>`, dont l'interface ne se laisse pas habiller comme la maquette et
 * dont le micro et la camera demarrent coupes. Ici on pilote la salle : le micro est ouvert
 * des l'arrivee (la camera aussi pour un appel video), et chaque ecran dessine ce qu'il veut
 * a partir de `participants()`.
 *
 * Un seul appel a la fois (c'est la regle de `CallService`) : service racine, `rejoindre`
 * est idempotent par salle. La salle reste connectee quand l'appel est minimise — seul
 * `quitter()` la ferme.
 */
@Injectable({ providedIn: 'root' })
export class SalleAppel {
  private readonly fabrique = inject(FABRIQUE_SALLE);

  readonly etat = signal<EtatSalle>('inactive');
  readonly erreur = signal<string | null>(null);
  readonly avertissement = signal<Avertissement>(null);
  /** Soi-meme en premier, puis les participants distants. */
  readonly participants = signal<ParticipantSalle[]>([]);
  readonly micro = signal(false);
  readonly camera = signal(false);
  readonly partage = signal(false);
  /** Vrai quand l'appareil a au moins deux cameras (telephone) : seul cas ou « Changer de camera » a un sens. */
  readonly plusieursCameras = signal(false);

  private salle: SalleClient | null = null;
  private salleRejointe: string | null = null;
  private generation = 0;
  private enAttente: Promise<void> | null = null;
  private dernieresOptions: OptionsRejoindre | null = null;

  /** Le partage d'ecran n'existe pas sur la plupart des navigateurs mobiles. */
  get partagePossible(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
  }

  /** Rejoint la salle. Rappeler pour la MEME salle ne fait rien ; une autre salle ferme d'abord la precedente. */
  rejoindre(options: OptionsRejoindre): Promise<void> {
    if (this.salleRejointe === options.roomName && this.enAttente) return this.enAttente;
    const lancement = this.demarrer(options);
    this.enAttente = lancement;
    return lancement;
  }

  private async demarrer(options: OptionsRejoindre): Promise<void> {
    this.dernieresOptions = options;
    await this.fermer();
    const generation = ++this.generation;
    this.salleRejointe = options.roomName;
    this.etat.set('connexion');
    this.erreur.set(null);
    this.avertissement.set(null);

    try {
      const { serverUrl, token } = await this.demanderJeton(options.authUrl, options.roomName);
      if (generation !== this.generation) return;

      const salle = await this.fabrique();
      this.brancher(salle, generation);
      await salle.connect({ serverUrl, token });
      if (generation !== this.generation) {
        await salle.disconnect();
        return;
      }
      this.salle = salle;
      this.etat.set('connectee');

      await this.publier('micro', () => salle.publishMicrophone());
      if (options.camera) await this.publier('camera', () => salle.publishCamera());
      this.rafraichir();
    } catch (e) {
      if (generation !== this.generation) return;
      this.erreur.set(e instanceof Error ? e.message : String(e));
      this.etat.set('erreur');
      this.salleRejointe = null;
      this.enAttente = null;
    }
  }

  /** Une permission refusee n'empeche pas l'appel : on y reste, sans ce peripherique, et on le dit. */
  private async publier(quoi: 'micro' | 'camera', action: () => Promise<void>): Promise<void> {
    try {
      await action();
    } catch (e) {
      const refus = (e as { code?: string; name?: string } | null)?.code === 'DEVICE_PERMISSION_DENIED' || (e as { name?: string } | null)?.name === 'NotAllowedError';
      if (!refus) throw e;
      this.avertissement.set(quoi === 'micro' ? 'micro_refuse' : 'camera_refusee');
    }
  }

  private async demanderJeton(authUrl: string, roomName: string): Promise<{ serverUrl: string; token: string }> {
    const separateur = authUrl.includes('?') ? '&' : '?';
    const reponse = await fetch(`${authUrl}${separateur}room=${encodeURIComponent(roomName)}`);
    if (!reponse.ok) throw new Error(`jeton refuse (${reponse.status})`);
    const donnees = (await reponse.json()) as Record<string, unknown> | null;
    const serverUrl = donnees?.['serverUrl'];
    const token = donnees?.['token'];
    if (typeof serverUrl !== 'string' || !serverUrl.trim() || typeof token !== 'string' || !token.trim()) {
      throw new Error("reponse du service d'appel invalide");
    }
    return { serverUrl, token };
  }

  private brancher(salle: SalleClient, generation: number): void {
    const maj = () => {
      if (generation === this.generation) this.rafraichir();
    };
    for (const e of ['participantConnected', 'participantDisconnected', 'trackSubscribed', 'trackUnsubscribed'] as const) {
      salle.on(e, maj);
    }
    salle.on('reconnecting', () => generation === this.generation && this.etat.set('reconnexion'));
    salle.on('reconnected', () => {
      if (generation !== this.generation) return;
      this.etat.set('connectee');
      this.rafraichir();
    });
    // Deconnexion SUBIE seulement : `quitter()` invalide la generation avant de fermer.
    salle.on('disconnected', () => {
      if (generation !== this.generation) return;
      this.etat.set('coupee');
      this.participants.set([]);
    });
    for (const e of EVENEMENTS_BRUTS) salle.raw.on(e, maj);
  }

  /** Relit la salle : le seul endroit qui fabrique `participants()`. */
  private rafraichir(): void {
    const salle = this.salle;
    if (!salle) return;
    const local = salle.raw.localParticipant;
    const piste = (source: string) => local.getTrackPublication(source)?.track?.mediaStreamTrack ?? null;

    const moi: ParticipantSalle = {
      identity: local.identity,
      moi: true,
      videoTrack: local.isCameraEnabled ? piste('camera') : null,
      audioTrack: null,
      micro: local.isMicrophoneEnabled,
      camera: !!local.isCameraEnabled,
      parle: local.isSpeaking,
      qualite: qualite(local.connectionQuality),
    };
    const distants = salle.getRemoteParticipants().map((p): ParticipantSalle => {
      const brut = salle.raw.remoteParticipants.get(p.identity);
      const video = p.screenShareTrack ?? p.videoTrack ?? null;
      return {
        identity: p.identity,
        moi: false,
        videoTrack: video,
        audioTrack: p.audioTrack ?? null,
        micro: brut?.isMicrophoneEnabled ?? !!p.audioTrack,
        camera: video !== null,
        parle: brut?.isSpeaking ?? false,
        qualite: qualite(brut?.connectionQuality),
      };
    });
    this.participants.set([moi, ...distants]);
    this.micro.set(moi.micro);
    this.camera.set(moi.camera);
    this.partage.set(local.isScreenShareEnabled);
    void this.detecterCameras();
  }

  private async detecterCameras(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return;
    try {
      const appareils = await navigator.mediaDevices.enumerateDevices();
      this.plusieursCameras.set(appareils.filter((d) => d.kind === 'videoinput').length > 1);
    } catch {
      this.plusieursCameras.set(false);
    }
  }

  /** Nouvelle tentative apres une coupure ou une erreur, avec les memes parametres. */
  reessayer(): Promise<void> {
    const options = this.dernieresOptions;
    if (!options) return Promise.resolve();
    this.salleRejointe = null;
    this.enAttente = null;
    return this.rejoindre(options);
  }

  async basculerMicro(): Promise<void> {
    await this.basculer('micro', this.micro(), () => this.salle!.publishMicrophone(), () => this.salle!.unpublishMicrophone());
  }

  async basculerCamera(): Promise<void> {
    await this.basculer('camera', this.camera(), () => this.salle!.publishCamera(), () => this.salle!.unpublishCamera());
  }

  async basculerPartage(): Promise<void> {
    if (!this.salle) return;
    try {
      if (this.partage()) await this.salle.stopScreenShare();
      else await this.salle.startScreenShare();
    } catch {
      /* refus de l'utilisateur ou navigateur sans partage : l'etat affiche reste celui d'avant */
    }
    this.rafraichir();
  }

  /** Passe a la camera suivante (avant / arriere sur un telephone). Sans autre camera, ne fait rien. */
  async changerCamera(): Promise<void> {
    if (!this.salle || typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return;
    const cameras = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput' && d.deviceId);
    if (cameras.length < 2) return;
    const actuelle = this.salle.raw.localParticipant.getTrackPublication('camera')?.track?.mediaStreamTrack.getSettings().deviceId;
    const suivante = cameras[(cameras.findIndex((c) => c.deviceId === actuelle) + 1) % cameras.length];
    try {
      await this.salle.switchCameraDevice(suivante.deviceId);
    } catch {
      /* camera indisponible : on garde l'actuelle */
    }
    this.rafraichir();
  }

  private async basculer(quoi: 'micro' | 'camera', actif: boolean, ouvrir: () => Promise<void>, fermer: () => Promise<void>): Promise<void> {
    if (!this.salle) return;
    try {
      if (actif) await fermer();
      else await ouvrir();
      this.avertissement.set(null);
    } catch (e) {
      const refus = (e as { code?: string; name?: string } | null)?.code === 'DEVICE_PERMISSION_DENIED' || (e as { name?: string } | null)?.name === 'NotAllowedError';
      if (refus) this.avertissement.set(quoi === 'micro' ? 'micro_refuse' : 'camera_refusee');
    }
    this.rafraichir();
  }

  /** Ferme la salle. Sans effet quand rien n'est ouvert. */
  async quitter(): Promise<void> {
    this.generation++;
    this.enAttente = null;
    this.salleRejointe = null;
    await this.fermer();
    this.etat.set('inactive');
    this.erreur.set(null);
    this.avertissement.set(null);
    this.participants.set([]);
    this.micro.set(false);
    this.camera.set(false);
    this.partage.set(false);
    this.plusieursCameras.set(false);
  }

  private async fermer(): Promise<void> {
    const salle = this.salle;
    this.salle = null;
    if (salle) {
      try {
        await salle.disconnect();
      } catch {
        /* LegbaClientRoom.disconnect() ne leve pas ; garde-fou pour un double de test */
      }
    }
  }
}

function qualite(valeur: string | undefined): QualiteConnexion {
  return valeur === 'excellent' || valeur === 'good' || valeur === 'poor' || valeur === 'lost' ? valeur : 'unknown';
}
